/**
 * CSS Custom Properties (Variables) for Mintytheme
 *
 * Following ReactJS standards for styling:
 * - CSS custom properties for theming
 * - Consistent spacing, typography, and colors
 * - Responsive design patterns
 */

import { css } from '@emotion/react';
import type { AppTheme } from '@/types/theme';

/**
 * Generate CSS custom properties for a theme
 *
 * @param theme - The theme object
 * @returns CSS string with custom properties
 */
export const createThemeVariables = (theme: AppTheme) => css`
  :root {
    /* Primary Colors */
    --bs-primary: ${theme.palette.primary.main};
    --bs-primary-contrast: ${theme.palette.primary.contrastText};
    --bs-primary-dark: ${theme.palette.primary.dark};
    --bs-primary-light: ${theme.palette.primary.light};

    /* Secondary Colors */
    --bs-secondary: ${theme.palette.secondary.main};
    --bs-secondary-contrast: ${theme.palette.secondary.contrastText};
    --bs-secondary-dark: ${theme.palette.secondary.dark};
    --bs-secondary-light: ${theme.palette.secondary.light};

    /* Status Colors */
    --bs-info: ${theme.palette.info.main};
    --bs-info-contrast: ${theme.palette.info.contrastText};
    --bs-success: ${theme.palette.success.main};
    --bs-success-contrast: ${theme.palette.success.contrastText};
    --bs-warning: ${theme.palette.warning.main};
    --bs-warning-contrast: ${theme.palette.warning.contrastText};
    --bs-error: ${theme.palette.error.main};
    --bs-error-contrast: ${theme.palette.error.contrastText};

    /* Background Colors */
    --bs-background: ${theme.palette.background.default};
    --bs-surface: ${theme.palette.background.paper};

    /* Text Colors */
    --bs-text-primary: ${theme.palette.text.primary};
    --bs-text-secondary: ${theme.palette.text.secondary};
    --bs-text-disabled: ${theme.palette.text.disabled};

    /* Action Colors */
    --bs-action-active: ${theme.palette.action.active};
    --bs-action-hover: ${theme.palette.action.hover};
    --bs-action-disabled: ${theme.palette.action.disabled};
    --bs-action-disabled-background: ${theme.palette.action.disabledBackground};

    /* Divider */
    --bs-divider: ${theme.palette.divider};

    /* Spacing Scale */
    --bs-spacing-1: ${theme.spacing(1)};
    --bs-spacing-2: ${theme.spacing(2)};
    --bs-spacing-3: ${theme.spacing(3)};
    --bs-spacing-4: ${theme.spacing(4)};
    --bs-spacing-5: ${theme.spacing(5)};
    --bs-spacing-6: ${theme.spacing(6)};
    --bs-spacing-8: ${theme.spacing(8)};
    --bs-spacing-10: ${theme.spacing(10)};
    --bs-spacing-12: ${theme.spacing(12)};
    --bs-spacing-16: ${theme.spacing(16)};

    /* Typography */
    --bs-font-family: ${theme.typography.fontFamily};
    --bs-font-size-xs: 0.75rem;
    --bs-font-size-sm: 0.875rem;
    --bs-font-size-base: 1rem;
    --bs-font-size-lg: 1.125rem;
    --bs-font-size-xl: 1.25rem;
    --bs-font-size-2xl: 1.5rem;
    --bs-font-size-3xl: 1.875rem;
    --bs-font-size-4xl: 2.25rem;

    /* Line Heights */
    --bs-line-height-none: 1;
    --bs-line-height-tight: 1.25;
    --bs-line-height-snug: 1.375;
    --bs-line-height-normal: 1.5;
    --bs-line-height-relaxed: 1.625;
    --bs-line-height-loose: 2;

    /* Font Weights */
    --bs-font-weight-light: 300;
    --bs-font-weight-normal: 400;
    --bs-font-weight-medium: 500;
    --bs-font-weight-semibold: 600;
    --bs-font-weight-bold: 700;

    /* Border Radius */
    --bs-border-radius-none: 0;
    --bs-border-radius-sm: 0.125rem;
    --bs-border-radius: 0.25rem;
    --bs-border-radius-md: 0.375rem;
    --bs-border-radius-lg: 0.5rem;
    --bs-border-radius-xl: 0.75rem;
    --bs-border-radius-2xl: 1rem;
    --bs-border-radius-full: 9999px;

    /* Shadows */
    --bs-shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --bs-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    --bs-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --bs-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --bs-shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

    /* Z-Index Scale */
    --bs-z-dropdown: 1000;
    --bs-z-sticky: 1020;
    --bs-z-fixed: 1030;
    --bs-z-modal-backdrop: 1040;
    --bs-z-modal: 1050;
    --bs-z-popover: 1060;
    --bs-z-tooltip: 1070;

    /* Transitions */
    --bs-transition-all: all 0.15s ease-in-out;
    --bs-transition-fade: opacity 0.15s linear;
    --bs-transition-collapse: height 0.35s ease;

    /* Breakpoints (for reference in JavaScript) */
    --bs-breakpoint-xs: 0;
    --bs-breakpoint-sm: 600px;
    --bs-breakpoint-md: 900px;
    --bs-breakpoint-lg: 1200px;
    --bs-breakpoint-xl: 1536px;
  }
`;

/**
 * Global styles for consistent theming
 */
export const globalStyles = css`
  /* Reset and base styles */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html {
    line-height: var(--bs-line-height-normal);
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
  }

  body {
    margin: 0;
    font-family: var(--bs-font-family);
    font-size: var(--bs-font-size-base);
    font-weight: var(--bs-font-weight-normal);
    line-height: var(--bs-line-height-normal);
    color: var(--bs-text-primary);
    background-color: var(--bs-background);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Focus styles for accessibility */
  :focus-visible {
    outline: 2px solid var(--bs-primary);
    outline-offset: 2px;
  }

  /* Link styles */
  a {
    color: var(--bs-primary);
    text-decoration: none;
    transition: var(--bs-transition-all);
  }

  a:hover {
    color: var(--bs-primary-dark);
    text-decoration: underline;
  }

  /* Button reset */
  button {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: inherit;
  }

  /* Utility classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .transition-all {
    transition: var(--bs-transition-all);
  }

  .text-primary {
    color: var(--bs-text-primary);
  }

  .text-secondary {
    color: var(--bs-text-secondary);
  }

  .bg-surface {
    background-color: var(--bs-surface);
  }
`;
